import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar.jsx';
import MobileTopBar from './MobileTopBar.jsx';
import AiKeyBanner from './AiKeyBanner.jsx';
import { Announcer } from '../util/a11y.jsx';
import { TITLE_BY_PATH } from '../nav.js';
import { api } from '../api.js';
import ErrorBoundary from './ErrorBoundary.jsx';
import CompanyResearchModal from './CompanyResearchModal.jsx';
import InterviewPrepModal from './InterviewPrepModal.jsx';
import { useArtifact, closeArtifact, publishRecPatch } from '../util/artifactHost.js';

// Sidebar + content shell. Every authenticated route renders inside this.
// Pages render only their own content — no per-page header.
// T121 — below md the fixed Sidebar hides itself and MobileTopBar (hamburger +
// slide-over drawer) takes over; the flex-col column keeps the top bar full
// width above the content. Desktop (md+) is unchanged: fixed rail + p-8.

// T250 — the document title. Every route left it at the literal "hunter", so
// browser history, tab strips and screen-reader route announcements were all
// the same word ten times over. Derived here rather than per page: the shell
// already knows the route, and asking ten pages to remember would guarantee
// the eleventh forgot.
//
// T433 — and derived from the ROUTE REGISTRY rather than a second list kept
// here. The second list is exactly how `/tasks` and `/mail` ended up with no
// title at all: both were added to nav.js and neither to this map, so the two
// pages he opens most read a bare "hunter". Same defect T250 fixed, found
// again because the fix lived in a copy.

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const mainRef = useRef(null);
  const first = useRef(true);

  // T410 — count which surfaces are actually opened.
  //
  // P4 of the UX roadmap proposes merging Documents into Profile and moving
  // Sources; both are guesses without this. The AI ledger shows what he
  // GENERATES, and a page can be opened every day while producing no rows —
  // Sources and Analytics are exactly that shape.
  //
  // Here rather than in ten pages, for the same reason the title is: the
  // shell already knows the route, and the eleventh page would forget.
  //
  // Fire and forget, and that has to be true of the SYNCHRONOUS path too —
  // the first version chained `.catch` straight onto the call, which throws
  // if the call itself throws before returning a promise. That is not a
  // hypothetical: it broke five existing tests whose `api` mock has no
  // `post`. A counter that can break a page render is worse than no counter.
  useEffect(() => {
    try {
      void Promise.resolve(api.post('/telemetry/visit', { route: pathname })).catch(() => {});
    } catch {
      /* counting is never worth a blank page */
    }
  }, [pathname]);

  useEffect(() => {
    const name = TITLE_BY_PATH[pathname];
    document.title = name ? `${name} · hunter` : 'hunter';

    // A client-side route change moves nothing for a keyboard or screen-reader
    // user: focus stays wherever the link was and the viewport keeps its old
    // scroll. Skip the very first render so a deep link does not steal focus
    // from whatever the browser restored.
    if (first.current) {
      first.current = false;
      return;
    }
    window.scrollTo(0, 0);
    mainRef.current?.focus();
  }, [pathname]);

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-100">
      {/* T250 — the sidebar precedes <main> in the DOM, so without this every
          keyboard user tabs through the whole nav on every page. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[100] focus:rounded-md focus:bg-brand-600 focus:px-3 focus:py-2 focus:text-sm focus:text-white"
      >
        Skip to content
      </a>
      {/* T255 — always mounted: a polite region must exist before it speaks. */}
      <Announcer />
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <MobileTopBar />
        <main id="main" ref={mainRef} tabIndex={-1} className="flex-1 overflow-x-hidden outline-none">
          <div className="mx-auto max-w-5xl p-4 md:p-8">
            <AiKeyBanner />
            {children}
          </div>
        </main>
      </div>
      {/* T422a — mounted ONCE, for every page, instead of twice.
          Inside its own boundary: the per-route boundary wraps {children},
          and this sits outside it, so an exception while rendering research
          would otherwise escape to the app-level boundary and take the
          sidebar with it. The review of the drawer plan named this exactly.
          Safe to leave mounted because T475 made the billing fetch depend on
          having a company name, not on being mounted. */}
      <ErrorBoundary section="Artefacts">
        <ArtifactHost />
      </ErrorBoundary>
    </div>
  );
}

/**
 * The one mount for the artefact overlays.
 *
 * Renders nothing until something is open, so the shell costs a closed set
 * lookup per navigation. A kind it does not recognise renders nothing rather
 * than reaching for a component that is not there — the same closed-enum
 * rule the deep-link handler uses (T422-0).
 */
function ArtifactHost() {
  const open = useArtifact();
  if (!open) return null;
  if (open.kind === 'company-research') {
    return <CompanyResearchModal companyName={open.rec?.job?.company ?? ''} onClose={closeArtifact} />;
  }
  if (open.kind === 'interview-prep') {
    return (
      <InterviewPrepModal
        rec={open.rec}
        onClose={closeArtifact}
        // The shell holds no row cache. It says what changed and each mounted
        // page applies it with the writeback it already has (T474).
        onUpdated={(patch) => publishRecPatch(open.rec?.id, patch)}
      />
    );
  }
  return null;
}
